import React, { useState, useEffect } from "react";
import { X, Loader2, Rocket } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = ["INITIAL_INSTALL", "UPDATE", "ROLLBACK", "REBUILD"];
const ENVS = ["PRODUCTION", "STAGING", "PREVIEW"];

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

/**
 * DeploymentFormDialog — buat deployment baru (Platform Owner).
 * Props: tenants (list), open, onClose, onCreated
 */
export default function DeploymentFormDialog({ tenants = [], open, onClose, onCreated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      tenant_id: tenants[0]?.tenant_id || "",
      deployment_type: "INITIAL_INSTALL",
      environment: "PRODUCTION",
      deployment_version: "",
      notes: "",
    });
  }, [open, tenants]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.tenant_id) { toast.error("Pilih tenant dulu."); return; }
    setBusy(true);
    try {
      const payload = {
        tenant_id: form.tenant_id,
        deployment_type: form.deployment_type,
        environment: form.environment,
      };
      if (form.deployment_version) payload.deployment_version = form.deployment_version;
      if (form.notes) payload.notes = form.notes;
      const r = await api.post("/platform/deployments", payload);
      toast.success(`Deployment ${r.data.deployment.deployment_number} dibuat.`);
      onCreated?.(r.data.deployment);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal membuat deployment"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="deployment-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">Buat Deployment</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Tenant *</label>
            <Select value={form.tenant_id} onValueChange={(v) => set({ tenant_id: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="dep-form-tenant"><SelectValue placeholder="Pilih tenant…" /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id} className={ITEM_CLS}>{t.tenant_name} · {t.tenant_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Type</label>
              <Select value={form.deployment_type} onValueChange={(v) => set({ deployment_type: v })}>
                <SelectTrigger className={TRIGGER_CLS} data-testid="dep-form-type"><SelectValue /></SelectTrigger>
                <SelectContent className={CONTENT_CLS}>
                  {TYPES.map((t) => (<SelectItem key={t} value={t} className={ITEM_CLS}>{t.replace("_", " ")}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Environment</label>
              <Select value={form.environment} onValueChange={(v) => set({ environment: v })}>
                <SelectTrigger className={TRIGGER_CLS} data-testid="dep-form-env"><SelectValue /></SelectTrigger>
                <SelectContent className={CONTENT_CLS}>
                  {ENVS.map((e) => (<SelectItem key={e} value={e} className={ITEM_CLS}>{e}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Version (opsional, auto v1, v2, …)</label>
            <Input value={form.deployment_version} onChange={(e) => set({ deployment_version: e.target.value })}
              placeholder="v1.2.0" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="dep-form-version" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Notes (opsional)</label>
            <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder:text-white/30 min-h-[70px]"
              placeholder="Catatan deployment…" data-testid="dep-form-notes" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.tenant_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="dep-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Membuat…</> : <><Rocket size={14} className="mr-1" /> Buat</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
